import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useRouteError } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { rootRoute } from 'routes/routes';
import { Button } from '@/components/ui/button';

const AUTO_RELOAD_KEY = 'error-page-auto-reloaded';

const ErrorPage = (): JSX.Element => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const error = useRouteError();

  useEffect(() => {
    const alreadyReloaded = sessionStorage.getItem(AUTO_RELOAD_KEY);
    if (!alreadyReloaded) {
      sessionStorage.setItem(AUTO_RELOAD_KEY, 'true');
      window.location.reload();
    }
  }, []);

  // Log full error details for debugging, but do not expose them to the user

  if (error) {
    console.error('[ErrorPage]', error);
  }

  const handleGoHome = () => {
    sessionStorage.removeItem(AUTO_RELOAD_KEY);
    navigate(rootRoute);
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-muted px-4">
      <div className="flex w-full max-w-md flex-col items-center gap-6 text-center">
        <div className="rounded-full bg-destructive/10 p-6">
          <AlertTriangle className="h-12 w-12 text-destructive" />
        </div>

        <div className="space-y-2">
          <h1 className="font-serif text-2xl leading-8">{t('error-page-title', 'Something went wrong')}</h1>
          <p className="text-sm text-muted-foreground">
            {t('error-page-description', 'An unexpected error occurred. We apologize for the inconvenience.')}
          </p>
        </div>

        <Button onClick={handleGoHome} className="w-full max-w-xs">
          {t('error-page-go-home', 'Go Home')}
        </Button>

        <p className="text-xs text-muted-foreground">
          {t('error-page-support-prompt', 'Need help? Contact us at')}{' '}
          <a href="mailto:support@demand-iq.com" className="font-medium text-link hover:underline">
            support@demand-iq.com
          </a>
        </p>
      </div>
    </div>
  );
};

export default ErrorPage;
